import {useState, useEffect, useMemo, useRef} from "react";
import {useReactToPrint} from "react-to-print";
import {
  Activity,
  MousePointer,
  Clock,
  Play,
  Calendar,
  X,
  Download,
  FileText,
  ArrowUpDown,
  User,
} from "lucide-react";
import {parsePhoneNumber} from "libphonenumber-js";

const OrbitLoader = () => (
  <div className="relative h-10 w-10 animate-spin">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rounded-full"></div>
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800/50 rounded-full"></div>
    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-800/50 rounded-full"></div>
    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-800/50 rounded-full"></div>
  </div>
);

const StatCard = ({icon, label, value, color}) => (
  <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-5 hover:shadow-md transition-shadow print:break-inside-avoid">
    <div className={`p-3 rounded-2xl ${color}`}>{icon}</div>
    <div>
      <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">
        {label}
      </p>
      <p className="text-2xl font-black">{value}</p>
    </div>
  </div>
);

const convertToE164 = (phoneNumberString, defaultCountryCode) => {
  try {
    const phoneNumber = parsePhoneNumber(phoneNumberString, defaultCountryCode);
    return phoneNumber && phoneNumber.isValid()
      ? phoneNumber.format("E.164")
      : phoneNumberString;
  } catch (error) {
    return phoneNumberString;
  }
};

const Account = ({loggedInUser}) => {
  const [user, setUser] = useState(null);
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortBy, setSortBy] = useState("time");

  const printRef = useRef(null);

  // PDF Export - Using logic from AdminDashboard to prevent cropping
  const downloadPDF = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Activity_Report_${loggedInUser?.customer_id || "User"}`,
    pageStyle: `
      @page { 
        margin: 15mm; 
        size: A4;
      }
      @media print {
        body { 
          -webkit-print-color-adjust: exact !important; 
          print-color-adjust: exact !important; 
        }
        main * { 
          overflow: visible !important; 
          height: auto !important; 
          max-height: none !important;
          min-height: auto !important;
        }
        html, body, #root {
          height: auto !important;
          overflow: visible !important;
        }
        .print\\:hidden { display: none !important; }
      }
    `,
  });

  // Suppression of react-to-print warnings as seen in AdminDashboard
  useEffect(() => {
    const originalWarn = console.warn;
    console.warn = (...args) => {
      if (args[0]?.includes?.("react-to-print.mjs")) return;
      originalWarn(...args);
    };
    return () => {
      console.warn = originalWarn;
    };
  }, []);

  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        setIsLoading(true);
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Basic ${process.env.REACT_APP_AWS_USER_TOKEN}`,
        };
        const e164Phone = convertToE164(loggedInUser.customer_id, "US");

        const [userRes, videoRes] = await Promise.all([
          fetch(
            `${process.env.REACT_APP_AWS_USER_ENDPOINT}/get-contact/id/${e164Phone}`,
            {headers},
          ),
          fetch(`${process.env.REACT_APP_AWS_USER_ENDPOINT}/get-videos`, {
            headers,
          }),
        ]);

        const userDataJson = await userRes.json();
        const videoDataJson = await videoRes.json();

        if (videoDataJson.result === "success") setVideos(videoDataJson.data);
        if (userDataJson.result === "success" && userDataJson.data.length > 0) {
          setUser(userDataJson.data[0]);
        }
      } catch (err) {
        console.error("Failed to fetch account data", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccountData();
  }, [loggedInUser.customer_id]);

  const getVideoInfo = (id) => {
    const video = videos.find((v) => v.id.toString() === id.toString());
    return video
      ? {title: video.title, category: video.category}
      : {title: `Video #${id}`, category: "General"};
  };

  const stats = useMemo(() => {
    if (!user) return null;

    const totals = {clicks: 0, secondsActive: 0, mouseMoves: 0, videoSecs: 0};
    const history = {};

    const isWithinRange = (dateStr) => {
      if (!startDate && !endDate) return true;
      const date = new Date(dateStr);
      const start = startDate ? new Date(startDate) : new Date("1970-01-01");
      const end = endDate ? new Date(endDate) : new Date("2099-12-31");
      return date >= start && date <= end;
    };

    if (user.activity) {
      Object.entries(user.activity).forEach(([date, day]) => {
        if (isWithinRange(date)) {
          totals.clicks += day.clicks || 0;
          totals.secondsActive += day.secondsActive || 0;
          totals.mouseMoves += day.mouseMoves || 0;
          totals.videoSecs += day.totalVideoSeconds || 0;
        }
      });
    }

    if (user.watch_history) {
      Object.entries(user.watch_history).forEach(([videoId, data]) => {
        const time = Object.entries(data.dailyBreakdown || {}).reduce(
          (sum, [date, val]) => (isWithinRange(date) ? sum + val : sum),
          0,
        );
        if (time > 0) history[videoId] = time;
      });
    }

    const sorted = Object.entries(history).sort((a, b) => {
      if (sortBy === "name")
        return getVideoInfo(a[0])
          .title.toLowerCase()
          .localeCompare(getVideoInfo(b[0]).title.toLowerCase());
      return b[1] - a[1];
    });

    return {totals, sorted};
  }, [user, startDate, endDate, sortBy, videos]);

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const secs = Math.floor(s % 60);
    return `${h > 0 ? h + "h " : ""}${m > 0 ? m + "m " : ""}${secs}s`;
  };

  const downloadCSV = () => {
    if (!stats) return;
    const {totals, sorted} = stats;
    const rows = [
      ["Metric", "Value"],
      ["Customer", `${user.customer_firstname} ${user.customer_lastname}`],
      ["ID", user.customer_id],
      ["Date Range", `${startDate || "Start"} to ${endDate || "End"}`],
      ["Clicks", totals.clicks],
      ["Interactions", totals.mouseMoves],
      ["Platform Time", totals.secondsActive],
      ["Hours Reported (Playtime)", totals.videoSecs],
      ["", ""],
      ["Video ID", "Video Title", "Category", "Time Watched (Seconds)"],
    ];

    sorted.forEach(([id, secs]) => {
      const info = getVideoInfo(id);
      rows.push([id, info.title, info.category, secs.toFixed(2)]);
    });

    const csvContent =
      "data:text/csv;charset=utf-8," + rows.map((e) => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `my_analytics_${user.customer_id}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading)
    return (
      <div className="flex h-screen items-center justify-center">
        <OrbitLoader />
      </div>
    );
  if (!user) return <div className="p-10 text-center">User not found.</div>;

  return (
    <div className="flex h-full w-full font-sans print:block print:h-auto">
      <main
        ref={printRef}
        className="flex-1 max-w-8xl mx-auto p-6 lg:p-12 overflow-y-auto print:overflow-visible print:h-auto print:p-0"
      >
        <div class="w-full overflow-hidden bg-blue-50 border-y border-blue-100 shadow-sm mb-8 rounded-[1rem]">
          <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div class="flex items-center justify-between flex-wrap md:flex-nowrap">
              <div class="flex items-center">
                <div class="flex-shrink-0">
                  <svg
                    class="h-6 w-6 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                    />
                  </svg>
                </div>
                <div class="ml-4">
                  <p class="font-medium text-blue-900 space-x-2">
                    <span className="font-bold">Benefits Requirement:</span>
                    <span className="block sm:inline">
                      Members must report X hours per month. This platform
                      tracks your hours automatically.
                    </span>
                  </p>
                </div>
              </div>

              <div class="mt-4 md:mt-0 w-full md:w-auto flex-shrink-0">
                <a
                  href="https://communityconnectlabs.com/health-plans-work-requirements/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 transition-colors"
                >
                  Learn more
                  <svg
                    class="ml-2 h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 print:pb-8 print:border-b print:border-gray-200">
          <div className="flex items-center gap-5">
            <div className="h-20 w-20 bg-blue-600 rounded-3xl flex items-center justify-center text-white shadow-lg shadow-blue-200">
              <User size={40} strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-4xl font-black text-gray-900 leading-tight">
                My Account
              </h1>
              <p className="text-gray-500 font-medium">
                {user.customer_firstname} {user.customer_lastname} •{" "}
                {user.customer_id}
              </p>
            </div>
          </div>

          <div className="flex flex-col items-end gap-3 print:hidden">
            <div className="flex gap-2">
              <button
                onClick={downloadCSV}
                className="p-2.5 bg-white border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors shadow-sm flex items-center gap-2 text-sm font-bold"
              >
                <Download size={16} /> CSV
              </button>
              <button
                onClick={downloadPDF}
                className="p-2.5 bg-gray-800 border border-gray-800 rounded-xl text-white hover:bg-gray-900 transition-colors shadow-sm flex items-center gap-2 text-sm font-bold"
              >
                <FileText size={16} /> PDF
              </button>
            </div>

            <div className="flex items-center gap-2 bg-gray-50 p-2 rounded-xl border border-gray-200 w-full sm:w-auto">
              <Calendar size={16} className="text-gray-400 ml-2" />
              <input
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-transparent text-sm font-bold focus:outline-none"
              />
              <span className="text-gray-300">|</span>
              <input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-transparent text-sm font-bold focus:outline-none"
              />
              {(startDate || endDate) && (
                <button
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                  }}
                  className="p-1 text-red-400"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10 print:grid-cols-2">
          <StatCard
            icon={<MousePointer size={22} />}
            label="Clicks"
            value={stats.totals.clicks}
            color="bg-orange-50 text-orange-600"
          />
          <StatCard
            icon={<Activity size={22} />}
            label="Interactions"
            value={stats.totals.mouseMoves.toLocaleString()}
            color="bg-blue-50 text-blue-600"
          />
          <StatCard
            icon={<Clock size={22} />}
            label="Platform Time"
            value={formatTime(stats.totals.secondsActive)}
            color="bg-emerald-50 text-emerald-600"
          />
          <StatCard
            icon={<Play size={22} />}
            label="Hours Reported (Playtime)"
            value={formatTime(stats.totals.videoSecs)}
            color="bg-violet-50 text-violet-600"
          />
        </div>

        <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm print:shadow-none print:border-gray-200 mb-4">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800">
              Learning Progress
            </h2>
            <div className="flex items-center gap-2 text-gray-500 print:hidden">
              <ArrowUpDown size={14} />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-xs font-bold uppercase tracking-widest focus:outline-none cursor-pointer bg-transparent"
              >
                <option value="time">Time Watched</option>
                <option value="name">Alphabetical</option>
              </select>
            </div>
          </div>

          <div className="space-y-6">
            {stats.sorted.map(([id, secs]) => {
              const info = getVideoInfo(id);
              const percentage = Math.min(
                (secs / (stats.totals.videoSecs || 1)) * 100,
                100,
              );
              return (
                <div
                  key={id}
                  className="group print:break-inside-avoid print:mb-4"
                >
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <h4 className="font-bold text-gray-800 transition-colors">
                        {info.title}
                      </h4>
                      <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded font-bold uppercase tracking-tighter">
                        {info.category}
                      </span>
                    </div>
                    <span className="text-sm font-black text-gray-700">
                      {formatTime(secs)}
                    </span>
                  </div>
                  <div className="h-3 w-full bg-gray-50 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all duration-1000"
                      style={{width: `${percentage}%`}}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex text-sm text-right bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-100 text-[12px] print:text-base print:justify-start print:gap-2">
          <p className="font-bold text-gray-400 uppercase">
            Last Recorded Activity:
          </p>
          <p className="text-gray-400">
            {user.timestamp ? new Date(user.timestamp).toLocaleString() : "N/A"}
          </p>
        </div>
      </main>

      <style>{`
        @media print {
          @page {
            size: auto;
            margin: 15mm;
          }
          html, body, #root {
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
            overflow: visible !important;
            display: block !important;
          }
          main, main * {
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
            overflow: visible !important;
            display: block !important;
          }
          .grid, [class*="grid"] {
            display: block !important;
            overflow: visible !important;
          }
          .print\\:hidden { display: none !important; }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            box-sizing: border-box !important;
          }
          .rounded-2xl, .rounded-3xl, [class*="p-"] {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Account;
